<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  CreateImageStepUpload,
  CreateSnapshotStepUploadMutation,
  CreateSnapshotStepUploadMutationVariables,
  CreateVideoStepUpload,
  FormVideoStepUploadDataQuery,
  FormVideoStepUploadDataQueryVariables,
  PermissionOrganization,
  UpdateImageStepUpload,
  UpdateSnapshotCaptionMutation,
  UpdateSnapshotCaptionMutationVariables,
  UpdateVideoStepUpload,
} from '@/generated/graphql'
import { computed, reactive, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import { useQuery, useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import TextField from '@/components/input/TextField.vue'
import { caption, fragmentInMillis } from '@/components/stepUpload/stepUploadValidation'
import Checkbox from '@/components/input/Checkbox.vue'
import ViewVideoStepUpload from '@/components/stepUpload/ViewVideoStepUpload.vue'
import MilliQuantityField from '@/components/input/MilliQuantityField.vue'
import AsyncDialog from '@/components/dialogs/AsyncDialog.vue'
import useAuthorization from '@/composables/useAuthorization'
import FormImageStepUpload from '@/components/stepUpload/FormImageStepUpload.vue'

type Model = CreateVideoStepUpload & UpdateVideoStepUpload
const model = defineModel<Model>({ required: true })
const props = defineProps<{
  uploadId: string
  stepId?: string
}>()
const emit = defineEmits<{
  snapshotCreated: []
}>()

const { t } = useI18n()

const { reactiveOrgAuthz } = useAuthorization()
const canSnapshot = reactiveOrgAuthz(
  PermissionOrganization.OrgProductWrite,
  PermissionOrganization.OrgUploadWrite,
)

const validation = useVuelidate<Model>(
  {
    caption,
    fragmentStartInMillis: fragmentInMillis,
    fragmentEndInMillis: fragmentInMillis,
  },
  model,
)

const onRangeCheck = () => {
  const limit = max.value
  const clamp = (value?: number) => (limit && value && value > limit ? limit : value)

  model.value.fragmentStartInMillis = clamp(model.value.fragmentStartInMillis)
  model.value.fragmentEndInMillis = clamp(model.value.fragmentEndInMillis)

  const start = model.value.fragmentStartInMillis
  const end = model.value.fragmentEndInMillis
  if (start && end && end < start) {
    model.value.fragmentStartInMillis = end
    model.value.fragmentEndInMillis = start
  }
}

const max = ref<number>()
const onDuration = (millis: number) => {
  max.value = millis
}

const rangeModel = computed(() => {
  const start = model.value.fragmentStartInMillis
  const end = model.value.fragmentEndInMillis

  return [start || 0, end || max.value || 0]
})
const onRangeModel = (range: number[]) => {
  model.value.fragmentStartInMillis = range[0]
  model.value.fragmentEndInMillis = range[1]
}

import { VideoExtension } from '@/generated/graphql'

const fetchQuery = useQuery<FormVideoStepUploadDataQuery, FormVideoStepUploadDataQueryVariables>(
  gql`
    query FormVideoStepUploadData($id: ID!) {
      upload {
        upload(id: $id) {
          extension {
            ... on VideoExtension {
              url
            }
          }
        }
      }
    }
  `,
  () => ({ id: props.uploadId }),
)
const extension = computed<VideoExtension | undefined>(
  () => fetchQuery.result.value?.upload?.upload?.extension as VideoExtension | undefined,
)
const videoUrl = computed(() => extension.value?.url)

const currentTimeInMillis = ref<number | null>(null)
const onCurrentTimeInMillis = (value: number) => {
  currentTimeInMillis.value = value
}

const snapshotDialog = ref<InstanceType<typeof AsyncDialog>>()
const snapshotUploadId = ref<string>()
const snapshotCreating = ref(false)
const snapshotCreatedNotify = ref(false)
const snapshotModel = reactive<Partial<UpdateImageStepUpload>>({})

const snapshotValidation = useVuelidate<Partial<UpdateImageStepUpload>>(
  {
    caption,
  },
  snapshotModel,
)

const createSnapshotMutation = useMutation<
  CreateSnapshotStepUploadMutation,
  CreateSnapshotStepUploadMutationVariables
>(gql`
  mutation CreateSnapshotStepUpload($command: CreateSnapshotStepUpload!) {
    product {
      createSnapshotStepUpload(command: $command) {
        id
        upload {
          id
        }
      }
    }
  }
`)

const updateCaptionMutation = useMutation<
  UpdateSnapshotCaptionMutation,
  UpdateSnapshotCaptionMutationVariables
>(gql`
  mutation UpdateSnapshotCaption($command: UpdateImageStepUpload!) {
    product {
      updateImageStepUpload(command: $command) {
        id
      }
    }
  }
`)

const snapshotDialogResolve = ref<(val?: (() => Promise<void>) | undefined) => void>()

const onConfirmSnapshot = async () => {
  await updateCaptionMutation.mutate({
    command: snapshotModel as UpdateImageStepUpload,
  })
  snapshotDialogResolve.value?.()
}

const openSnapshotDialog = async () => {
  if (!snapshotDialog.value || !currentTimeInMillis.value || !props.stepId) {
    return
  }

  snapshotUploadId.value = undefined
  snapshotModel.id = undefined
  snapshotModel.caption = model.value.caption || ''
  snapshotCreating.value = true

  const dialogPromise = snapshotDialog.value.open()

  const result = await createSnapshotMutation.mutate({
    command: {
      stepId: props.stepId,
      attachmentId: model.value.id || undefined,
      uploadId: model.value.id ? undefined : props.uploadId,
      timestampInMillis: Math.round(currentTimeInMillis.value),
    },
  })

  const created = result?.data?.product?.createSnapshotStepUpload
  snapshotCreating.value = false
  if (!created) return

  snapshotModel.id = created.id
  snapshotUploadId.value = created.upload.id
  emit('snapshotCreated')
  snapshotCreatedNotify.value = true

  await dialogPromise
}

defineExpose({ validation })
</script>

<template>
  <div class="text-center mt-3">
    <view-video-step-upload
      v-if="!!videoUrl"
      :src="videoUrl"
      :options="model"
      :show-snapshot="!!props.stepId"
      :snapshot-authorized="canSnapshot"
      height="100%"
      width="100%"
      @current-time-in-millis="onCurrentTimeInMillis"
      @snapshot="openSnapshotDialog"
      @duration-in-millis="onDuration"
    />
  </div>
  <text-field
    v-model="model.caption"
    :validation="validation.caption"
    :label="t('entity.stepUpload.field.caption')"
  />
  <v-range-slider
    :max="max"
    :min="0"
    :step="100"
    :model-value="rangeModel"
    hide-details
    @update:model-value="onRangeModel"
  />
  <v-row>
    <v-col cols="4">
      <milli-quantity-field
        v-model="model.fragmentStartInMillis"
        :validation="validation.fragmenStartInMillis"
        :label="t('entity.stepUpload.field.fragmentStart')"
        @blur="onRangeCheck"
      />
    </v-col>
    <v-col cols="4">
      <milli-quantity-field
        v-model="model.fragmentEndInMillis"
        :validation="validation.fragmenEndInMillis"
        :label="t('entity.stepUpload.field.fragmentEnd')"
        @blur="onRangeCheck"
      />
    </v-col>
    <v-col>
      <checkbox
        v-model="model.fragmentLoop"
        class="mt-7"
        :label="t('entity.stepUpload.field.fragmentLoop')"
      />
    </v-col>
  </v-row>

  <async-dialog
    v-if="props.stepId"
    ref="snapshotDialog"
    :validation="snapshotValidation"
    max-width="650"
  >
    <template #title>
      {{ t('entity.stepUpload.snapshot.dialogTitle') }}
    </template>
    <div v-if="snapshotCreating" class="text-center py-8">
      <v-progress-circular indeterminate />
    </div>
    <template v-else-if="snapshotUploadId">
      <form-image-step-upload
        v-model="snapshotModel as CreateImageStepUpload & UpdateImageStepUpload"
        :upload-id="snapshotUploadId"
      />
      <v-btn
        color="primary"
        class="float-right"
        :loading="updateCaptionMutation.loading.value"
        @click="onConfirmSnapshot"
      >
        {{ t('button.add') }}
      </v-btn>
    </template>
    <template #actions="{ resolve }">
      {{ void (snapshotDialogResolve = resolve) }}
      <v-btn color="primary" @click="resolve()">
        {{ t('button.close') }}
      </v-btn>
    </template>
  </async-dialog>

  <v-snackbar v-model="snapshotCreatedNotify" :timeout="5000">
    {{ t('entity.stepUpload.snapshot.created') }}
  </v-snackbar>
</template>

<style scoped lang="scss"></style>
